/**
 * Mandatory training — the rule that decides whether a volunteer may be active.
 *
 * Parinaam requires every volunteer to complete a mandatory training, and the
 * date they last completed it is what the rest of the system reasons about:
 *
 *   - a volunteer cannot be *activated* without a current training on file, and
 *   - one whose training has lapsed is swept back to INACTIVE, and
 *   - a reminder goes out before it lapses.
 *
 * The window and the lead time are the same two numbers everywhere — the API
 * that gates activation, the sweep that inactivates, the reminder that warns, and
 * the badge the profile shows — so they live here, once, and both the frontend
 * and the backend import them. A rule split across two files drifts the first
 * time one side is tuned.
 *
 * Every function takes `today` explicitly rather than reading the clock, so the
 * result is a pure function of its inputs and a test can pin any date it likes.
 */
/** How long a completed training stays valid. Parinaam's decision: one year. */
export const TRAINING_VALIDITY_MONTHS = 12;
/** How many days before it lapses the reminder goes out. */
export const TRAINING_REMINDER_LEAD_DAYS = 30;
/** The day a training completed on `completedOn` stops being valid, or null. */
export function trainingExpiresOn(completedOn) {
    if (!completedOn)
        return null;
    const date = new Date(`${completedOn}T00:00:00Z`);
    if (Number.isNaN(date.getTime()))
        return null;
    date.setUTCMonth(date.getUTCMonth() + TRAINING_VALIDITY_MONTHS);
    return date.toISOString().slice(0, 10);
}
function addDays(isoDate, days) {
    const date = new Date(`${isoDate}T00:00:00Z`);
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
}
/**
 * A volunteer's training standing on a given day.
 *
 * ISO 'YYYY-MM-DD' strings compare correctly with `<`/`>=`, so the arithmetic is
 * a date shift and the comparisons are plain string comparisons.
 */
export function trainingStatus(completedOn, today) {
    const expires = trainingExpiresOn(completedOn);
    if (!expires)
        return 'NONE';
    if (today >= expires)
        return 'EXPIRED';
    if (today >= addDays(expires, -TRAINING_REMINDER_LEAD_DAYS))
        return 'EXPIRING_SOON';
    return 'CURRENT';
}
/**
 * Whether this training standing permits activation.
 *
 * CURRENT and EXPIRING_SOON both do — someone a fortnight from lapsing is still
 * trained today, and blocking them would be stricter than the rule. NONE and
 * EXPIRED do not.
 */
export function isTrainingValidForActivation(status) {
    return status === 'CURRENT' || status === 'EXPIRING_SOON';
}
//# sourceMappingURL=training.js.map